import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'

import MemberRoleView from './MemberRole'
import '../ProjectSettings.css'

import { DeleteButton } from '../../utils/Buttons'
import { displayError, systemError } from '../../utils/Errors'
import { DropTargetViewSmall } from '../../utils/DropTargetView'
import DropTarget from '../../utils/DropTarget'
import { isAllowedImage } from '../../images/AllowedImages'
import { MemberDisplay } from '../../utils/MemberDisplay'
import { resizeImage } from '../../utils/ImageResizer'

import { Member, MemberRole } from '../../../models3/Member'

import { Root } from '../../../models3/Root'

type MemberDropTargetProps = {
    member: Member
    rt: Root
}

const MemberDropTarget: FC<MemberDropTargetProps> = observer(({ member, rt, children }) => {
    const { t } = useTranslation()

    async function upload(files: FileList) {
        const { iAmAdmin, username, project } = rt

        if (!iAmAdmin && username !== member.email) {
            displayError(t('Unless you are an admin, you may only upload a member image for yourself.'))
            return
        }

        if (files.length !== 1) {
            displayError(t('You must drop exactly one file.'))
            return
        }

        const file = files[0]
        if (!isAllowedImage(file.type)) {
            displayError(t('Cannot upload this type of file.'))
            return
        }

        const maxFileSize = 50 * 1024 * 1024
        if (file.size > maxFileSize) {
            displayError(t('File must be smaller than 50MB.'))
            return
        }

        try {
            const resizedUrl = await resizeImage(file, 64, 64)
            await project.setMemberImage(member.email, resizedUrl)
        } catch (error) {
            systemError(error)
        }
    }

    const message = <div />
    const dropTargetView = <DropTargetViewSmall {...{ message }} />

    return <DropTarget {...{ upload, dropTargetView }}>{children}</DropTarget>
})

interface IMemberView {
    member: Member
    rt: Root
    isSoleAdmin: boolean
}

const MemberView: FC<IMemberView> = ({ member, rt, isSoleAdmin }) => {
    const { t } = useTranslation()
    const { iAmAdmin } = rt

    const onSetRole = (role: MemberRole) => {
        rt.project.setMemberRole(member.email, role).catch(displayError)
    }

    const removeMember = () => {
        rt.project.removeMember(member.email).catch(displayError)
    }

    return (
        <tr>
            <td>
                <MemberDropTarget {...{ rt, member }}>
                    <MemberDisplay member={member} imageSize="large" showFullName />
                </MemberDropTarget>
            </td>
            <td className="vertically-centered-text">
                <MemberRoleView member={member} rt={rt} _onChange={onSetRole} isSoleAdmin={isSoleAdmin} />
            </td>
            <td>
                {iAmAdmin && !isSoleAdmin && (
                    <DeleteButton
                        className="sl-delete-button"
                        tooltip={t('Remove this member from project')}
                        enabled
                        onClick={removeMember}
                    />
                )}
            </td>
        </tr>
    )
}

export default MemberView
